import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Checkbox, FormControlLabel, Chip,
  CircularProgress, TextField, Divider, Paper
} from '@material-ui/core'
import axios from 'axios'

const BID_SCOPES = ['Demo', 'Grading', 'Drainage', 'Irrigation', 'Hardscape', 'Softscape', 'Lighting', 'Water Feature', 'Cleanup']

const TakeoffRequestModal = ({ open, onClose, doc, projectId, project, url }) => {
  const [vendors, setVendors] = useState([])
  const [selected, setSelected] = useState({}) // { vendorId: { checked, scopes: Set } }
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [notes, setNotes] = useState('')
  const [previewVendorId, setPreviewVendorId] = useState(null)

  useEffect(() => {
    if (!open || !url) return
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/vendors/by-capability?type=takeoff`)
      .then(res => {
        setVendors(res.data || [])
        setSelected({})
        setNotes('')
        setPreviewVendorId(null)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [open, url])

  const toggleVendor = (vendorId) => {
    setSelected(prev => {
      const cur = prev[vendorId]
      if (cur?.checked) {
        const next = { ...prev }
        delete next[vendorId]
        return next
      }
      return { ...prev, [vendorId]: { checked: true, scopes: new Set() } }
    })
  }

  const toggleScope = (vendorId, scope) => {
    setSelected(prev => {
      const cur = prev[vendorId]
      if (!cur) return prev
      const scopes = new Set(cur.scopes)
      if (scopes.has(scope)) scopes.delete(scope)
      else scopes.add(scope)
      return { ...prev, [vendorId]: { ...cur, scopes } }
    })
  }

  const selectedCount = Object.values(selected).filter(v => v.checked && v.scopes.size > 0).length

  const buildPreview = (vendor) => {
    if (!vendor?.capability) return { subject: '', body: '' }
    const template = vendor.capability.emailBodyTemplate || 'Plans: {{plansUrl}}'
    const subjectTmpl = vendor.capability.emailSubjectTemplate || 'Takeoff Request: {{projectName}}'
    const scopes = selected[vendor._id]?.scopes || new Set()
    const vars = {
      projectName: project?.name || '',
      clientName: project?.client || '',
      plansUrl: doc?.s3Url || '',
      requestedScopes: Array.from(scopes).join(', ') || '(none selected)',
      contactName: vendor.capability.contactName || vendor.name,
      senderName: '(your name)',
      senderEmail: '(your email)'
    }
    const replace = (t) => t.replace(/\{\{(\w+)\}\}/g, (m, k) => vars[k] || m)
    return { subject: replace(subjectTmpl), body: replace(template) }
  }

  const handleSend = async () => {
    setSending(true)
    try {
      const requests = Object.entries(selected)
        .filter(([, v]) => v.checked && v.scopes.size > 0)
        .map(([vendorId, v]) => ({
          vendorId,
          capabilityType: 'takeoff',
          requestedScopes: Array.from(v.scopes),
          plansUrl: doc?.s3Url || '',
          plansFileName: doc?.name || doc?.originalFilename || '',
          notes
        }))

      await axios.post(`${url}/api/bid-estimation/projects/${projectId}/vendor-requests`, { requests })
      onClose('sent', requests.length)
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message))
    } finally {
      setSending(false)
    }
  }

  if (!doc) return null

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Request Takeoff</DialogTitle>
      <DialogContent>
        {/* Plan info */}
        <Box mb={2} p={1.5} style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {doc.name || doc.originalFilename}
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ wordBreak: 'break-all' }}>
            {doc.s3Url}
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : vendors.length === 0 ? (
          <Typography color="textSecondary" style={{ padding: 16, textAlign: 'center' }}>
            No vendors with takeoff capability found. Add capabilities to your vendors first.
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
              Select Vendors & Scopes
            </Typography>
            {vendors.map(v => {
              const sel = selected[v._id]
              const isChecked = sel?.checked || false
              return (
                <Paper key={v._id} variant="outlined" style={{ padding: 12, marginBottom: 8 }}>
                  <FormControlLabel
                    control={<Checkbox checked={isChecked} onChange={() => toggleVendor(v._id)} />}
                    label={
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>{v.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {v.capability?.contactEmail || '(no email)'}
                        </Typography>
                      </Box>
                    }
                  />
                  {isChecked && (
                    <Box ml={4} mt={1}>
                      <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                        {BID_SCOPES.map(scope => (
                          <Chip
                            key={scope}
                            label={scope}
                            size="small"
                            clickable
                            color={sel.scopes.has(scope) ? 'primary' : 'default'}
                            onClick={() => toggleScope(v._id, scope)}
                            style={{ fontSize: 11 }}
                          />
                        ))}
                      </Box>
                      <Button
                        size="small"
                        style={{ marginTop: 8, textTransform: 'none', fontSize: 11 }}
                        onClick={() => setPreviewVendorId(previewVendorId === v._id ? null : v._id)}
                      >
                        {previewVendorId === v._id ? 'Hide Preview' : 'Preview Email'}
                      </Button>
                      {previewVendorId === v._id && (() => {
                        const { subject, body } = buildPreview(v)
                        return (
                          <Box mt={1} p={1.5} style={{ backgroundColor: '#fafafa', borderRadius: 4, border: '1px solid #e0e0e0' }}>
                            <Typography variant="caption" style={{ fontWeight: 600 }}>To: {v.capability?.contactEmail}</Typography>
                            <Typography variant="caption" display="block">Subject: {subject}</Typography>
                            <Divider style={{ margin: '8px 0' }} />
                            <Typography variant="caption" style={{ whiteSpace: 'pre-wrap' }}>{body}</Typography>
                          </Box>
                        )
                      })()}
                    </Box>
                  )}
                </Paper>
              )
            })}

            <TextField
              label="Notes (optional)"
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ marginTop: 16 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={sending || selectedCount === 0}
        >
          {sending ? <CircularProgress size={16} color="inherit" /> : `Send ${selectedCount} Request${selectedCount !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TakeoffRequestModal
